/* eslint-disable react/forbid-component-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSpring } from 'react-spring';
import { useTranslation } from 'react-i18next';
import { Headline, SelectField } from '@oneaudi/unified-web-alpha-components';
import {
  DropDownWrapper,
  DropDownInput,
  DropDownSelect,
  ListOfOptions,
  TopicOption,
  TitlePlaceholder,
  SelectedPlaceholder,
  ArrowIcons,
  Checkbox,
  HiddenInput,
  Title,
  DropDown,
  DropdownLabel,
  SubLabel,
} from './DropDownSelector.styles';
import useAnimations from '../../../hooks/useAnimations';
import { selectInputs, setUserInputs } from '../../../redux/inputsReducer';
import { selectErrors, clearError, setError } from '../../../redux/errorsReducer';
import { digitalLeadsFormInput } from '../../../utils/analyticsEventListeners';
import ArrowUpIcon from '../../../assets/icons/ArrowUpIcon';
import ArrowDownIcon from '../../../assets/icons/ArrowDownIcon';
import { COLORS } from '../../../styles/variables';
import CheckMarkIcon from '../../../assets/icons/CheckMarkIcon';
import useClickOutside from '../../../hooks/useClickOutside';
import type { CheckBoxInputProps } from '../../../types';
import type { Form } from '../../../types/analytics';
import { Note, ErrorMessage } from '../TextInput/TextInput.styles';
import { OptionalNote } from '../CheckboxInput/CheckBoxInput.styles';
import { FEATURE_APP_DETAILS, INPUT_TYPE } from '../../../constants';
import { convertToPromotionsType } from '../../../functions/convertToPromotionsType';
import { selectDealerAndVehicleData } from '../../../redux/dealerAndVehicleReducer';
import { selectForm, setForm } from '../../../redux/formReducer';
import { selectAppConfig } from '../../../redux/appConfigReducer';
import { getVehicleForPromotions } from '../../../functions/getVehicleForPromotions';
import CautionIcon from '../../../assets/icons/CautionIcon';

interface DropDownSelectorProps {
  options: CheckBoxInputProps;
  title?: string;
  inputLabel: string;
  inputKey: string;
  inputNote?: string;
  isOptional?: boolean;
  dropdownHeight: number;
}

const DropDownSelector: React.FC<DropDownSelectorProps> = ({
  options,
  title,
  inputLabel,
  inputKey,
  inputNote,
  isOptional,
  dropdownHeight,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const inputs = useSelector(selectInputs);
  const errors = useSelector(selectErrors);
  const inputValue = inputs[inputKey]; // we store the option's *value* here
  const errorMessage = errors[inputKey];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const shouldApplyMargin =
    inputKey === INPUT_TYPE.CONTACT_TIME || inputKey === INPUT_TYPE.TEST_DRIVE_BOOKING_TIME;

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(
    isDropdownOpen,
    dropdownHeight,
  );

  const { offers, presetForm } = useSelector(selectAppConfig);
  const isGenericForm = presetForm === 'generic';
  const promotion = convertToPromotionsType(offers!);

  const listOfOptions = useMemo(() => Object.keys(options), [options]);

  const selectedOption = useMemo(() => {
    if (!inputValue) return null;
    const key = listOfOptions.find((k) => options[k]?.value === inputValue);
    return key ? options[key] : null;
  }, [inputValue, options, listOfOptions]);

  const isAnyTopicSelected = !!selectedOption;

  const inputAnimation = useSpring({
    to: {
      top: isAnyTopicSelected ? -22 : 4,
      fontSize: isAnyTopicSelected ? '12px' : '16px',
    },
  });

  // Detect form-wide validation by checking if sibling required fields have errors.
  // When text inputs get validated (via blur/submit), their errors appear in Redux.
  // If those errors exist and this dropdown is required but empty, we set our error too.
  const hasOtherValidationErrors = useMemo(() => {
    const siblingFields = ['firstName', 'lastName', 'zipCode', 'email'];
    return siblingFields.some((field) => !!errors[field]);
  }, [errors]);

  useEffect(() => {
    if (isOptional) return;
    if (!isGenericForm || inputKey !== INPUT_TYPE.TOPIC_OF_INTEREST) return;

    // Validate when: the dropdown was directly touched, OR form-wide validation has kicked in
    if (!isTouched && !hasOtherValidationErrors) return;

    const isEmpty = !inputValue || !inputValue.trim();

    // Only dispatch when the error state actually needs to change.
    // This prevents unnecessary re-renders while the user is typing in sibling fields.
    if (isEmpty && !errorMessage) {
      dispatch(
        setError({
          field: inputKey,
          error: t('digitalLeads.inputs.error.topicOfInterest'),
        }),
      );
    } else if (!isEmpty && errorMessage) {
      dispatch(clearError({ field: inputKey }));
    }
  }, [
    isTouched,
    hasOtherValidationErrors,
    inputValue,
    errorMessage,
    isOptional,
    isGenericForm,
    inputKey,
    dispatch,
    t,
  ]);

  const onHandleChange = useCallback(
    (value: string) => {
      setIsTouched(true);

      const inputTouched = {
        inputTitle: inputKey,
        inputValue: value,
        action: 'select',
        inputType: 'dropdown',
      };

      // Use vehicle for VTP, vehicleByCarline/vehicleByAudiCode for NGC/MLP, or offers data for promotions
      const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
      const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

      const updatedForm: Form = {
        ...contactForm,
        ...{ formData: { ...contactForm.formData, ...{ [inputKey]: value } } },
      };

      dispatch(setForm(updatedForm));
      dispatch(setUserInputs({ inputName: inputKey, inputValue: value }));

      // Handle error state based on value
      if (value.trim()) {
        dispatch(clearError({ field: inputKey }));
      } else if (isGenericForm && inputKey === INPUT_TYPE.TOPIC_OF_INTEREST) {
        dispatch(
          setError({
            field: inputKey,
            error: t('digitalLeads.inputs.error.topicOfInterest'),
          }),
        );
      }

      digitalLeadsFormInput(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);

      setIsDropdownOpen(false);
    },
    [
      inputKey,
      vehicle,
      vehicleByCarline,
      vehicleByAudiCode,
      offers,
      contactForm,
      dispatch,
      promotion,
      isGenericForm,
      t,
    ],
  );

  const onHandleChangeRef = useRef(onHandleChange);

  useEffect(() => {
    onHandleChangeRef.current = onHandleChange;
  }, [onHandleChange]);

  // Default to the only option (by *value*) when there is exactly one option,
  // but only if it's different from what's already selected.
  useEffect(() => {
    if (listOfOptions.length !== 1) return;

    const onlyKey = listOfOptions[0];
    const onlyValue = options[onlyKey]?.value ?? onlyKey;

    if (inputValue !== onlyValue) {
      onHandleChangeRef.current(onlyValue);
    }
  }, [listOfOptions, options, inputValue]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent, value: string) => {
    if (e.key !== ' ') return;
    e.preventDefault();
    onHandleChangeRef.current(value);
  }, []);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(
    dropdownRef,
    useCallback(() => {
      // Mark as touched when clicking outside while open (equivalent to blur for text inputs)
      if (isDropdownOpen) {
        setIsTouched(true);
      }
      setIsDropdownOpen(false);
    }, [isDropdownOpen]),
  );

  if (!process.env.IS_LEGACY) {
    return (
      <DropDownWrapper
        ref={dropdownRef}
        isOptional={isOptional}
        shouldApplyMargin={shouldApplyMargin}
        data-testid="dropdown-wrapper"
      >
        {title && (
          <Headline
            margin={['0', '0', '24px', '0']}
            variant={{ type: 'headline', order: '4', weight: 'normal' }}
            tag="h2"
          >
            {title}
          </Headline>
        )}
        <SelectField
          appendix={isOptional ? '(optional)' : ''}
          label={`${inputLabel} ${!isOptional ? '*' : ''}`}
          data-testid={`dropdown-selector-${inputKey}`}
          hint={inputNote}
          id="dropdown-selector"
          onChange={(e) => {
            onHandleChange(e.target.value);
          }}
          onBlur={() => {
            setIsTouched(true);
          }}
          value={inputValue || ''}
        >
          {listOfOptions.map((option) => {
            const { value, label, key } = options[option];
            const sublabel = options[option]?.subLabel;

            if (sublabel) {
              return (
                <optgroup key={key} label={label}>
                  <option data-testid={`dropdown-option-checkbox-${value}`} value={value}>
                    {sublabel}
                  </option>
                </optgroup>
              );
            }
            return (
              <option key={key} data-testid={`dropdown-option-checkbox-${value}`} value={value}>
                {label}
              </option>
            );
          })}
        </SelectField>
        {!!errorMessage && (
          <ErrorMessage data-testid={`dropdown-error-${inputKey}`}>
            <CautionIcon />
            {errorMessage}
          </ErrorMessage>
        )}
      </DropDownWrapper>
    );
  }

  return (
    <DropDownWrapper
      ref={dropdownRef}
      isOptional={isOptional}
      shouldApplyMargin={shouldApplyMargin}
      data-testid="dropdown-wrapper"
    >
      {title && <Title>{title}</Title>}
      <DropDownInput
        onClick={() => {
          setIsDropdownOpen((prev) => !prev);
        }}
        data-testid={`dropdown-trigger-${inputKey}`}
      >
        <TitlePlaceholder style={inputAnimation}>
          {inputLabel}
          {isOptional && <OptionalNote>(optional)</OptionalNote>}
        </TitlePlaceholder>
        {selectedOption && (
          <SelectedPlaceholder>
            {selectedOption.label}
            {selectedOption.subLabel && <SubLabel>{selectedOption.subLabel}</SubLabel>}
          </SelectedPlaceholder>
        )}
        <ArrowIcons>
          {isDropdownOpen ? (
            <ArrowUpIcon color={COLORS.black} />
          ) : (
            <ArrowDownIcon color={COLORS.black} />
          )}
        </ArrowIcons>
      </DropDownInput>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <DropDownSelect style={props}>
              <DropDown style={filtersAccordianAnimation} data-testid="dropdown-selector">
                <ListOfOptions>
                  {listOfOptions.map((optionKey) => {
                    const { value, label, key } = options[optionKey];
                    const sublabel = options[optionKey]?.subLabel;
                    const checked = inputValue === value;
                    return (
                      <TopicOption
                        key={key}
                        onClick={() => {
                          onHandleChangeRef.current(value);
                        }}
                      >
                        <Checkbox
                          tabIndex={0}
                          onClick={() => {
                            onHandleChangeRef.current(value);
                          }}
                          role="checkbox"
                          aria-checked={checked}
                          onKeyDown={(e) => {
                            handleKeyPress(e, value);
                          }}
                          checked={checked}
                          data-testid={`dropdown-option-checkbox-${value}`}
                        >
                          {checked && <CheckMarkIcon />}
                        </Checkbox>
                        <HiddenInput
                          type="checkbox"
                          onChange={() => {
                            onHandleChangeRef.current(value);
                          }}
                          checked={checked}
                          name={String(value)}
                        />
                        <DropdownLabel>
                          {label}
                          {sublabel && <SubLabel>{sublabel}</SubLabel>}
                        </DropdownLabel>
                      </TopicOption>
                    );
                  })}
                </ListOfOptions>
              </DropDown>
            </DropDownSelect>
          ),
      )}
      {!!errorMessage && (
        <ErrorMessage data-testid={`dropdown-error-${inputKey}`}>
          <CautionIcon />
          {errorMessage}
        </ErrorMessage>
      )}
      <Note>{inputNote}</Note>
    </DropDownWrapper>
  );
};

export default DropDownSelector;
